import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Phone,
  Mail,
  Calendar,
  Clock,
  Briefcase,
  Building2,
  MapPin,
  Edit,
} from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";

interface EmployeeAssignment {
  _id: Id<"locationEmployeeAssignments">;
  employeeId: Id<"employees">;
  employeeName: string;
  employeeEmail: string;
  employeePhone: string;
  position: string;
  shift: string;
  shiftId: string; // The shift ID for editing
  assignedAt: number;
}

interface ViewEmployeeAssignmentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: EmployeeAssignment | null;
  onEdit?: () => void;
}

export function ViewEmployeeAssignmentSheet({
  isOpen,
  onClose,
  assignment,
  onEdit,
}: ViewEmployeeAssignmentSheetProps) {
  // Fetch full employee details
  const employee = useQuery(
    api.employees.getEmployeeById,
    assignment ? { id: assignment.employeeId } : "skip"
  );

  if (!assignment) return null;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getShiftColor = (shift: string) => {
    switch (shift.toLowerCase()) {
      case "morning":
      case "day":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "evening":
      case "afternoon":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "night":
      case "overnight":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPositionColor = (position: string) => {
    switch (position.toLowerCase()) {
      case "supervisor":
      case "manager":
      case "lead":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "cleaner":
      case "janitor":
        return "bg-green-100 text-green-800 border-green-200";
      case "specialist":
      case "technician":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle className="text-xl font-semibold">{assignment.employeeName}</SheetTitle>
          <SheetDescription className="mt-2">
            View detailed information about this employee assignment
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Position and Shift */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className={`${getPositionColor(assignment.position)} border`}
                >
                  {assignment.position}
                </Badge>
                <Badge variant="outline" className={`${getShiftColor(assignment.shift)} border`}>
                  {assignment.shift}
                </Badge>
              </div>
              {onEdit && (
                <Button variant="outline" size="sm" onClick={onEdit} className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit Assignment
                </Button>
              )}
            </div>

            {/* Employee Information */}
            <Card className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <User className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Employee Information</h3>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Name:</span>
                  <span>{assignment.employeeName}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Email:</span>
                  <span className="text-blue-600">{assignment.employeeEmail}</span>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Phone:</span>
                  <span className="text-blue-600">{assignment.employeePhone}</span>
                </div>
                {employee?.position && (
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Position:</span>
                    <span>{employee.position}</span>
                  </div>
                )}
                {employee?.department && (
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Department:</span>
                    <span>{employee.department}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Assignment Details */}
            <Card className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">Assignment Details</h3>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Position:</span>
                  <Badge
                    variant="outline"
                    className={`${getPositionColor(assignment.position)} w-fit border`}
                  >
                    {assignment.position}
                  </Badge>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Shift:</span>
                  <Badge
                    variant="outline"
                    className={`${getShiftColor(assignment.shift)} w-fit border`}
                  >
                    {assignment.shift}
                  </Badge>
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                  <span className="font-medium text-gray-600">Assigned:</span>
                  <span>{formatDate(assignment.assignedAt)}</span>
                </div>
              </div>
            </Card>

            {/* Employee Details */}
            {employee && (
              <Card className="p-4">
                <div className="mb-4 flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold">Employee Details</h3>
                </div>
                <div className="space-y-3">
                  {employee.hireDate && (
                    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                      <span className="font-medium text-gray-600">Hire Date:</span>
                      <span>{employee.hireDate}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                    <span className="font-medium text-gray-600">Status:</span>
                    <Badge
                      variant="outline"
                      className={
                        employee.status === "active"
                          ? "border-green-200 bg-green-100 text-green-800 hover:bg-green-100"
                          : "border-red-200 bg-red-100 text-red-800 hover:bg-red-100"
                      }
                    >
                      {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                    </Badge>
                  </div>
                  {employee.position && (
                    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                      <span className="font-medium text-gray-600">Position:</span>
                      <span>{employee.position}</span>
                    </div>
                  )}
                  {employee.department && (
                    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                      <span className="font-medium text-gray-600">Department:</span>
                      <span>{employee.department}</span>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Assignment Timeline */}
            <Card className="p-4">
              <div className="mb-4 flex items-center gap-3">
                <Calendar className="h-5 w-5 text-orange-600" />
                <h3 className="text-lg font-semibold">Assignment Timeline</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-blue-50 p-3">
                  <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                  <div>
                    <p className="font-medium text-blue-900">Assignment Started</p>
                    <p className="text-sm text-blue-700">{formatDateTime(assignment.assignedAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-green-50 p-3">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-green-600"></div>
                  <div>
                    <p className="font-medium text-green-900">Currently Active</p>
                    <p className="text-sm text-green-700">Assignment is ongoing</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
